import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { API_URLS } from '../../../config/api';
import MediaSelector from '../MediaSelector';

const AdminSettings = () => {
  const [form, setForm] = useState({
    company_name: '',
    email: '',
    phone: '',
    address: '',
    directions_url: '',
    website: '',
    whatsapp: '',
    instagram: '',
    twitter: '',
    linkedin: '',
    favicon: ''
  });
  const [smtpForm, setSmtpForm] = useState({
    smtp_host: '',
    smtp_port: '',
    smtp_username: '',
    smtp_password: '',
    smtp_encryption: 'tls',
    smtp_from_name: '',
    smtp_from_email: ''
  });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [gtm, setGtm] = useState({ gtm_head: '', gtm_body: '' });
  const [paytrForm, setPaytrForm] = useState({
    merchant_id: '',
    merchant_key: '',
    merchant_salt: '',
    test_mode: true,
    is_active: false,
    max_installment: 12,
    timeout_limit: 30
  });
  
  const [consultationPrice, setConsultationPrice] = useState(500);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        // Fetch company settings
        const res = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=getCompanySettings`);
        const json = await res.json();
        if (json.status === 'success' && json.data) {
          setForm({
            company_name: json.data.company_name || '',
            email: json.data.email || '',
            phone: json.data.phone || '',
            address: json.data.address || '',
            directions_url: json.data.directions_url || '',
            website: json.data.website || '',
            whatsapp: json.data.whatsapp || '',
            instagram: json.data.instagram || '',
            twitter: json.data.twitter || '',
            linkedin: json.data.linkedin || '',
            favicon: json.data.favicon || ''
          });
        }
      } catch (e) {
        // noop
      }
    };

    const fetchSmtpSettings = async () => {
      try {
        const res = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=getSmtpSettings`);
        const json = await res.json();
        if (json.status === 'success' && json.data) {
          setSmtpForm({
            smtp_host: json.data.smtp_host || '',
            smtp_port: json.data.smtp_port || '',
            smtp_username: json.data.smtp_username || '',
            smtp_password: json.data.smtp_password || '',
            smtp_encryption: json.data.smtp_encryption || 'tls',
            smtp_from_name: json.data.smtp_from_name || '',
            smtp_from_email: json.data.smtp_from_email || ''
          });
        }
      } catch (e) {
        // noop
      }
    };

    const fetchPaytrSettings = async () => {
      try {
        const res = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=getPaytrSettings`);
        const json = await res.json();
        if (json.status === 'success' && json.data) {
          setPaytrForm({
            merchant_id: json.data.merchant_id || '',
            merchant_key: json.data.merchant_key || '',
            merchant_salt: json.data.merchant_salt || '',
            test_mode: json.data.test_mode === 1 || json.data.test_mode === true,
            is_active: json.data.is_active === 1 || json.data.is_active === true,
            max_installment: json.data.max_installment || 12,
            timeout_limit: json.data.timeout_limit || 30
          });
        }
      } catch (e) {
        // noop
      }
    };

    const fetchConsultationPrice = async () => {
      try {
        const apiBase = process.env.NODE_ENV === 'development' 
          ? 'http://localhost:8000' 
          : `${window.location.protocol}//${window.location.host}`;
        const res = await fetch(`${apiBase}/backend/api.php?action=getConsultationPrice`);
        const json = await res.json();
        if (json.status === 'success' && json.price) {
          setConsultationPrice(parseFloat(json.price));
        }
      } catch (e) {
        // noop
      }
    };

    fetchSettings();
    fetchSmtpSettings();
    fetchPaytrSettings();
    fetchConsultationPrice();
    (async () => {
      try {
        const res = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=getSiteScripts`);
        const json = await res.json();
        if (json.status === 'success' && json.data) {
          setGtm({ gtm_head: json.data.gtm_head || '', gtm_body: json.data.gtm_body || '' });
        }
      } catch (e) {}
    })();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleGtmSave = async () => {
    setLoading(true);
    setStatus(null);
    try {
      const res = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=updateSiteScripts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(gtm)
      });
      const json = await res.json();
      if (json.status === 'success') setStatus('GTM kodu kaydedildi');
      else setStatus('Hata: ' + (json.message || 'GTM kaydedilemedi'));
    } catch (e) {
      setStatus('Hata: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSmtpChange = (e) => {
    const { name, value } = e.target;
    setSmtpForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setLoading(true);
    setStatus(null);
    try {
      const res = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=updateCompanySettings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const json = await res.json();
      if (json.status === 'success') {
        setStatus('Kaydedildi');
      } else {
        setStatus('Hata: ' + (json.message || 'Kaydedilemedi'));
      }
    } catch (e) {
      setStatus('Hata: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSmtpSave = async () => {
    setLoading(true);
    setStatus(null);
    try {
      const res = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=updateSmtpSettings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(smtpForm)
      });
      const json = await res.json();
      if (json.status === 'success') {
        setStatus('SMTP ayarları kaydedildi');
      } else {
        setStatus('Hata: ' + (json.message || 'SMTP ayarları kaydedilemedi'));
      }
    } catch (e) {
      setStatus('Hata: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePaytrChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPaytrForm((prev) => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handlePaytrSave = async () => {
    setLoading(true);
    setStatus(null);
    try {
      const payload = {
        ...paytrForm,
        test_mode: paytrForm.test_mode ? 1 : 0,
        is_active: paytrForm.is_active ? 1 : 0,
        max_installment: Number(paytrForm.max_installment) || 12,
        timeout_limit: Number(paytrForm.timeout_limit) || 30
      };
      const res = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=updatePaytrSettings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const json = await res.json();
      if (json.status === 'success') {
        setStatus('PayTR ayarları kaydedildi');
      } else {
        setStatus('Hata: ' + (json.message || 'PayTR ayarları kaydedilemedi'));
      }
    } catch (e) {
      setStatus('Hata: ' + e.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleConsultationPriceSave = async () => {
    setLoading(true);
    setStatus(null);
    try {
      const res = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=updateConsultationPrice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ price: consultationPrice })
      });
      const json = await res.json();
      if (json.status === 'success') {
        setStatus('Danışmanlık ücreti güncellendi');
      } else {
        setStatus('Hata: ' + (json.message || 'Danışmanlık ücreti güncellenemedi'));
      }
    } catch (e) {
      setStatus('Hata: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 px-4 sm:px-6">
      <Card>
        <CardHeader>
          <CardTitle>Genel Ayarlar</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-600">İşletme Adı</label>
              <Input name="company_name" value={form.company_name} onChange={handleChange} />
            </div>
            <div>
              <label className="text-sm text-gray-600">Web Sitesi</label>
              <Input name="website" value={form.website} onChange={handleChange} />
            </div>
            <div>
              <label className="text-sm text-gray-600">E-posta</label>
              <Input name="email" value={form.email} onChange={handleChange} />
            </div>
            <div>
              <label className="text-sm text-gray-600">Telefon</label>
              <Input name="phone" value={form.phone} onChange={handleChange} />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm text-gray-600">Adres</label>
              <Textarea name="address" value={form.address} onChange={handleChange} />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm text-gray-600">Yol Tarifi URL</label>
              <Input 
                name="directions_url" 
                value={form.directions_url} 
                onChange={handleChange}
                placeholder="https://goo.gl/maps/... veya Google Maps linki"
              />
              <p className="text-xs text-gray-500 mt-1">
                İletişim sayfasındaki adres kartı bu URL'ye yönlendirecek
              </p>
            </div>
            <div>
              <label className="text-sm text-gray-600">WhatsApp</label>
              <Input name="whatsapp" value={form.whatsapp} onChange={handleChange} />
            </div>
            <div>
              <label className="text-sm text-gray-600">Instagram</label>
              <Input name="instagram" value={form.instagram} onChange={handleChange} />
            </div>
            <div>
              <label className="text-sm text-gray-600">Twitter/X</label>
              <Input name="twitter" value={form.twitter} onChange={handleChange} />
            </div>
            <div>
              <label className="text-sm text-gray-600">LinkedIn</label>
              <Input name="linkedin" value={form.linkedin} onChange={handleChange} />
            </div>
            <div className="md:col-span-2 space-y-2">
              <label className="text-sm text-gray-600">Favicon</label>
              <MediaSelector
                label="Favicon Seç"
                placeholder="Tarayıcı sekmesinde görünecek favicon görselini seçin"
                value={form.favicon}
                onChange={(url) =>
                  setForm((prev) => ({
                    ...prev,
                    favicon: url
                  }))
                }
              />
              <p className="text-xs text-gray-500">
                En iyi sonuç için kare formatta (32x32 veya 48x48) PNG / WebP / ICO görsel yükleyin.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={handleSave} disabled={loading}>{loading ? 'Kaydediliyor...' : 'Kaydet'}</Button>
            {status && <span className="text-sm text-gray-600">{status}</span>}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>SMTP Ayarları</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-600">SMTP Host</label>
              <Input name="smtp_host" value={smtpForm.smtp_host} onChange={handleSmtpChange} placeholder="smtp.gmail.com" />
            </div>
            <div>
              <label className="text-sm text-gray-600">SMTP Port</label>
              <Input name="smtp_port" value={smtpForm.smtp_port} onChange={handleSmtpChange} placeholder="587" />
            </div>
            <div>
              <label className="text-sm text-gray-600">SMTP Kullanıcı Adı</label>
              <Input name="smtp_username" value={smtpForm.smtp_username} onChange={handleSmtpChange} placeholder="your-email@gmail.com" />
            </div>
            <div>
              <label className="text-sm text-gray-600">SMTP Şifre</label>
              <Input name="smtp_password" type="password" value={smtpForm.smtp_password} onChange={handleSmtpChange} placeholder="••••••••" />
            </div>
            <div>
              <label className="text-sm text-gray-600">Şifreleme</label>
              <select name="smtp_encryption" value={smtpForm.smtp_encryption} onChange={handleSmtpChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="tls">TLS</option>
                <option value="ssl">SSL</option>
                <option value="none">None</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-gray-600">Gönderen Adı</label>
              <Input name="smtp_from_name" value={smtpForm.smtp_from_name} onChange={handleSmtpChange} placeholder="Website Al" />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm text-gray-600">Gönderen E-posta</label>
              <Input name="smtp_from_email" value={smtpForm.smtp_from_email} onChange={handleSmtpChange} placeholder="noreply@website-al.com" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={handleSmtpSave} disabled={loading}>{loading ? 'Kaydediliyor...' : 'SMTP Ayarlarını Kaydet'}</Button>
            {status && <span className="text-sm text-gray-600">{status}</span>}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>PayTR Ödeme Ayarları</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-600">Merchant ID *</label>
              <Input 
                name="merchant_id" 
                value={paytrForm.merchant_id} 
                onChange={handlePaytrChange} 
                placeholder="Mağaza numaranız" 
              />
            </div>
            <div>
              <label className="text-sm text-gray-600">Maksimum Taksit</label>
              <Input 
                name="max_installment" 
                type="number"
                value={paytrForm.max_installment} 
                onChange={handlePaytrChange} 
                placeholder="12" 
              />
            </div>
          </div>
          
          <div>
            <label className="text-sm text-gray-600">Merchant Key *</label>
            <Input 
              name="merchant_key" 
              type="password"
              value={paytrForm.merchant_key} 
              onChange={handlePaytrChange} 
              placeholder="API Key" 
            />
          </div>
          
          <div>
            <label className="text-sm text-gray-600">Merchant Salt *</label>
            <Input 
              name="merchant_salt" 
              type="password"
              value={paytrForm.merchant_salt} 
              onChange={handlePaytrChange} 
              placeholder="Güvenlik anahtarı" 
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-600">Timeout Süresi (dakika)</label>
              <Input 
                name="timeout_limit" 
                type="number"
                value={paytrForm.timeout_limit} 
                onChange={handlePaytrChange} 
                placeholder="30" 
              />
            </div>
          </div>

          <div className="flex items-center space-x-4 py-2">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input 
                type="checkbox"
                name="test_mode"
                checked={paytrForm.test_mode}
                onChange={handlePaytrChange}
                className="w-4 h-4 text-purple-600 rounded"
              />
              <span className="text-sm text-gray-700">Test Modu (Gerçek ödeme alınmaz)</span>
            </label>

            <label className="flex items-center space-x-2 cursor-pointer">
              <input 
                type="checkbox"
                name="is_active"
                checked={paytrForm.is_active}
                onChange={handlePaytrChange}
                className="w-4 h-4 text-purple-600 rounded"
              />
              <span className="text-sm text-gray-700">Aktif (Ödeme sistemi açık)</span>
            </label>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2">ℹ️ PayTR Entegrasyon Bilgileri</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Merchant bilgilerinizi PayTR hesabınızdan alabilirsiniz</li>
              <li>• Test modunda gerçek ödeme alınmaz, sadece test yapılır</li>
              <li>• Canlıya geçmeden önce test modunda tüm senaryoları deneyin</li>
              <li>• Aktif seçeneği ile ödeme sistemini açıp kapatabilirsiniz</li>
            </ul>
          </div>

          <div className="flex items-center gap-3">
            <Button onClick={handlePaytrSave} disabled={loading}>
              {loading ? 'Kaydediliyor...' : 'PayTR Ayarlarını Kaydet'}
            </Button>
            {status && <span className="text-sm text-gray-600">{status}</span>}
          </div>
        </CardContent>
      </Card>

      {/* Consultation Price Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Ücretli Danışmanlık Ayarları</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm text-gray-600 mb-2 block">
              Danışmanlık Ücreti (TL) *
            </label>
            <div className="flex items-center gap-4">
              <Input 
                type="number"
                step="0.01"
                value={consultationPrice} 
                onChange={(e) => setConsultationPrice(parseFloat(e.target.value) || 0)} 
                placeholder="500.00" 
                className="max-w-xs"
              />
              <span className="text-gray-600">₺</span>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Bu ücret, anasayfadaki "Ücretli Danışmanlık Al" butonunda ve ödeme formunda gösterilecektir.
            </p>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-semibold text-yellow-900 mb-2">💡 Bilgi</h4>
            <ul className="text-sm text-yellow-800 space-y-1">
              <li>• Ücreti değiştirdiğinizde tüm sayfalarda otomatik güncellenir</li>
              <li>• Ödeme PayTR üzerinden alınır</li>
              <li>• PayTR ayarlarının aktif olması gerekir</li>
            </ul>
          </div>

          <div className="flex items-center gap-3">
            <Button onClick={handleConsultationPriceSave} disabled={loading}>
              {loading ? 'Kaydediliyor...' : 'Ücreti Güncelle'}
            </Button>
            {status && <span className="text-sm text-gray-600">{status}</span>}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Google Tag Manager</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm text-gray-600">Head Kodu (En üst konuma yerleştirilir)</label>
            <p className="text-xs text-gray-500 mb-2">Bu kodu sayfanın &lt;head&gt; bölümünde mümkün olan en üst konuma yerleştiririz.</p>
            <textarea
              className="w-full border rounded-md p-3 min-h-[120px] font-mono text-xs"
              value={gtm.gtm_head}
              onChange={(e)=>setGtm(v=>({...v, gtm_head: e.target.value}))}
              placeholder={`<!-- Google Tag Manager -->\n<script>(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':\nnew Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],\nj=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=\n'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);\n})(window,document,'script','dataLayer','GTM-XXXXXXX');</script>\n<!-- End Google Tag Manager -->`}
            />
          </div>
          <div>
            <label className="text-sm text-gray-600">Body Kodu (Açılış &lt;body&gt; etiketinden hemen sonra)</label>
            <p className="text-xs text-gray-500 mb-2">Bu kodu açılış &lt;body&gt; etiketinden hemen sonra yerleştiririz.</p>
            <textarea
              className="w-full border rounded-md p-3 min-h-[120px] font-mono text-xs"
              value={gtm.gtm_body}
              onChange={(e)=>setGtm(v=>({...v, gtm_body: e.target.value}))}
              placeholder={`<!-- Google Tag Manager (noscript) -->\n<noscript><iframe src="https://www.googletagmanager.com/ns.html?id=GTM-XXXXXXX"\nheight="0" width="0" style="display:none;visibility:hidden"></iframe></noscript>\n<!-- End Google Tag Manager (noscript) -->`}
            />
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={handleGtmSave} disabled={loading}>{loading ? 'Kaydediliyor...' : 'GTM Kodlarını Kaydet'}</Button>
            {status && <span className="text-sm text-gray-600">{status}</span>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSettings;






